
import Vue from "vue";
import DateTimeRange from "@/components/DateTimeRange.vue";
import dayjs from "dayjs";

const FIELD_ORDER = [
  { label: "Acc X", type: "acceleration", axis: "x", key: "acc_x" },
  { label: "Acc Y", type: "acceleration", axis: "y", key: "acc_y" },
  { label: "Acc Z", type: "acceleration", axis: "z", key: "acc_z" },
  { label: "Vel X", type: "velocity", axis: "x", key: "vel_x" },
  { label: "Vel Y", type: "velocity", axis: "y", key: "vel_y" },
  { label: "Vel Z", type: "velocity", axis: "z", key: "vel_z" },
  { label: "Env X", type: "envelope", axis: "x", key: "env_x" },
  { label: "Env Y", type: "envelope", axis: "y", key: "env_y" },
  { label: "Env Z", type: "envelope", axis: "z", key: "env_z" },
  { label: "Temp", type: "temperature", axis: "z", key: "temp" },
];

export default Vue.extend({
  name: "StatisticalAlarmCalculation",
  components: { DateTimeRange },

  props: {
    sensor: { type: Object, required: true },
    modelCode: { type: String, required: true },
    modelId: { type: Number, required: true },
  },

  data() {
    return {
      dialog: false,
      step: 1,
      loading: false,
      dateTimeRangeKey: 0,
      startDate: dayjs().subtract(30, "day").format("YYYY-MM-DD") as string,
      endDate: dayjs().format("YYYY-MM-DD") as string,
      isNormal: true,
      thresholds: {
        acc_x: 0,
        acc_y: 0,
        acc_z: 0,
        vel_x: 0,
        vel_y: 0,
        vel_z: 0,
        env_x: 0,
        env_y: 0,
        env_z: 0,
        temp: 0,
      } as Record<string, number>,
      calculatedFields: [] as any[],
      stepLabels: ["Select data and threshold", "Select and revise alarm limit", "Review new alarm limit"],
      matrixRows: [
        { rowKey: "x", label: "P X-Axis", cells: ["acc_x", "vel_x", "env_x", "temp"] as (string | null)[] },
        { rowKey: "y", label: "P Y-Axis", cells: ["acc_y", "vel_y", "env_y", null] as (string | null)[] },
        { rowKey: "z", label: "P Z-Axis", cells: ["acc_z", "vel_z", "env_z", null] as (string | null)[] },
      ],
    };
  },

  computed: {
    dateRangeValid(): boolean {
      if (!this.startDate || !this.endDate) return false;
      const diff = (new Date(this.endDate).getTime() - new Date(this.startDate).getTime()) / 86400000;
      return diff >= 0 && diff <= 92;
    },

    dateRangeText(): string {
      if (!this.startDate || !this.endDate) return "";
      const fmt = (d: string) => (this as any).$date(d).format("DD/MM/YYYY");
      return `${fmt(this.startDate)} - ${fmt(this.endDate)}`;
    },

    fieldMap(): Record<string, any> {
      const map: Record<string, any> = {};
      for (const f of this.calculatedFields) {
        const def = FIELD_ORDER.find((d) => d.type === f.type && d.axis === f.axis);
        if (def) map[def.key] = f;
      }
      return map;
    },

    selectedFields(): any[] {
      return this.calculatedFields.filter((f: any) => f.selected && !f.noData);
    },

    insertCount(): number {
      return this.selectedFields.length;
    },
  },

  methods: {
    flatCells(row: { cells: (string | null)[]; rowKey: string }) {
      return row.cells.flatMap((cellKey, ci) => [
        { cellKey, ci, prop: "warning" as const, borderLeft: true, tdKey: "w-" + row.rowKey + "-" + ci },
        { cellKey, ci, prop: "alarm" as const, borderLeft: false, tdKey: "a-" + row.rowKey + "-" + ci },
      ]);
    },

    stepCircleClass(n: number): string {
      if (this.step > n) return "bg-[#34A853] text-white";
      if (this.step === n) return "bg-[var(--v-primary-base)] text-white";
      return "bg-white text-[#9e9e9e] !border border-solid border-[#9e9e9e]";
    },

    onDateRangeSubmit(ev: { startDate: string; endDate: string }) {
      this.startDate = dayjs(ev.startDate).subtract(7, "hour").format("YYYY-MM-DDTHH:mm:ss");
      this.endDate = dayjs(ev.endDate).subtract(7, "hour").format("YYYY-MM-DDTHH:mm:ss");
    },

    arrowSymbol(oldVal: any, newVal: any): string {
      if (oldVal === null || newVal === null) return "—";
      if (newVal > oldVal) return "↑";
      if (newVal < oldVal) return "↓";
      return "—";
    },

    existingValue(field: any) {
      if (field.type === "temperature") {
        return {
          warning: (this.sensor as any).temperature?.warning ?? 0,
          alarm: (this.sensor as any).temperature?.alarm ?? 0,
        };
      }
      const axis = (this.sensor as any)[field.type]?.[field.axis];
      return { warning: axis?.warning ?? 0, alarm: axis?.alarm ?? 0 };
    },

    async onNext() {
      if (this.step === 1) {
        await this.fetchAndCalculate();
      } else {
        this.step++;
      }
    },

    async fetchAndCalculate() {
      this.loading = true;
      try {
        const result = await (this as any).$dep.modelUseCase.getStatisticalAlarm({
          modelCode: this.modelCode,
          startDate: this.startDate!,
          endDate: this.endDate!,
          thresholds: this.thresholds,
          sensorNameId: (this.sensor as any).sensorId,
          isNormal: this.isNormal,
        });

        this.calculatedFields = FIELD_ORDER.map((fieldDef) => {
          const serverField = (result.fields || []).find((f: any) => f.type === fieldDef.type && f.axis === fieldDef.axis);
          const warning = serverField?.warning ?? null;
          const alarm = serverField?.alarm ?? null;
          return {
            ...fieldDef,
            warning,
            alarm,
            noData: warning === null,
            selected: warning !== null,
          };
        });

        this.step = 2;
      } catch (e) {
        console.error("Statistical alarm calculation failed", e);
      } finally {
        this.loading = false;
      }
    },

    onConfirm() {
      const updatedSensor = JSON.parse(JSON.stringify(this.sensor));
      this.selectedFields.forEach((f: any) => {
        if (f.type === "temperature") {
          updatedSensor.temperature.warning = f.warning;
          updatedSensor.temperature.alarm = f.alarm;
        } else {
          updatedSensor[f.type][f.axis].warning = f.warning;
          updatedSensor[f.type][f.axis].alarm = f.alarm;
        }
      });

      const sensors = [...(this as any).$store.state.updateModelForm.raw_vibration_sensor_in];
      const idx = sensors.findIndex((s: any) => s.sensorId === (this.sensor as any).sensorId);
      if (idx !== -1) sensors[idx] = updatedSensor;
      (this as any).$store.dispatch("setUpdateModelForm", {
        ...(this as any).$store.state.updateModelForm,
        raw_vibration_sensor_in: sensors,
      });
      this.onCancel();
    },

    onCancel() {
      this.dialog = false;
      this.step = 1;
      this.startDate = dayjs().subtract(30, "day").format("YYYY-MM-DD");
      this.endDate = dayjs().format("YYYY-MM-DD");
      this.isNormal = true;
      this.thresholds = {
        acc_x: 0,
        acc_y: 0,
        acc_z: 0,
        vel_x: 0,
        vel_y: 0,
        vel_z: 0,
        env_x: 0,
        env_y: 0,
        env_z: 0,
        temp: 0,
      };
      this.calculatedFields = [];
      this.dateTimeRangeKey++;
    },
  },
});
